'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@/components/ui/Badge';
import type { ProjectCardData } from '@/types';

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Thumbnail box is `h-44` (176px). Fixed-width carousel slides pass `fixedWidth` so the
 *  image emits a 1x/2x srcset instead of the full responsive ladder (~2 KB per card). */
const THUMB_HEIGHT = 176;

function ImageWithFallback({ src, fallbackSrc, alt, className, sizes, priority, fixedWidth }: {
  src: string; fallbackSrc: string; alt: string; className?: string; sizes?: string; priority?: boolean;
  fixedWidth?: number;
}) {
  const [imgSrc, setImgSrc] = useState(src);
  const handleError = () => setImgSrc(fallbackSrc);
  if (fixedWidth) {
    return (
      <Image
        src={imgSrc} alt={alt} width={fixedWidth} height={THUMB_HEIGHT}
        className={`${className ?? ''} h-full w-full`}
        priority={priority}
        onError={handleError}
      />
    );
  }
  return (
    <Image
      src={imgSrc} alt={alt} fill
      className={className} sizes={sizes}
      priority={priority}
      onError={handleError}
    />
  );
}

function useTilt() {
  return {
    onMouseMove(e: React.MouseEvent<HTMLElement>) {
      const el   = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const x    = (e.clientX - rect.left) / rect.width  - 0.5;
      const y    = (e.clientY - rect.top)  / rect.height - 0.5;
      el.style.transform = `perspective(700px) rotateX(${-y * 7}deg) rotateY(${x * 7}deg) scale3d(1.02,1.02,1.02)`;
    },
    onMouseLeave(e: React.MouseEvent<HTMLElement>) {
      e.currentTarget.style.transform = 'perspective(700px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    },
  };
}

const GITHUB_PATH = 'M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z';

// ── Props ──────────────────────────────────────────────────────────────────────

export interface ProjectCardLabels {
  viewProject: string;
  viewCode: string;
  details: string;
}

export interface ProjectCardProps {
  project: ProjectCardData;
  locale: string;
  labels: ProjectCardLabels;
  /** Show the ★ featured badge on the top-end corner */
  showFeaturedBadge?: boolean;
  /** Show the project's category (a link to its category page) above the title */
  showCategory?: boolean;
  /** Enable Framer Motion entrance animation + 3-D tilt (homepage showcase) */
  animated?: boolean;
  animationDelay?: number;
  /** Card background — 'surface' (default) or 'bg' for contrast inside surface sections */
  cardBg?: 'surface' | 'bg';
  /** Eagerly load the thumbnail with high fetch priority (use for above-the-fold/LCP cards) */
  priority?: boolean;
  /** Card is rendered at a fixed pixel width (carousel slide) — enables a lean 1x/2x image srcset */
  fixedWidth?: number;
  className?: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ProjectCard({
  project,
  locale,
  labels,
  showFeaturedBadge = false,
  showCategory = false,
  animated = false,
  animationDelay = 0,
  cardBg = 'surface',
  priority = false,
  fixedWidth,
  className = '',
}: ProjectCardProps) {
  const tilt  = useTilt();
  // Prefer the requested language, fall back to the other one so a project with
  // only one translation never renders an empty card or a broken /projects/undefined link.
  const title = (locale === 'ar' ? (project.title.ar || project.title.en) : (project.title.en || project.title.ar)) || project._id;
  const desc  = (locale === 'ar' ? (project.shortDescription.ar || project.shortDescription.en) : (project.shortDescription.en || project.shortDescription.ar)) || '';
  const slug  = (locale === 'ar' ? (project.slug.ar || project.slug.en) : (project.slug.en || project.slug.ar)) || project._id;
  const altText = (locale === 'ar' ? project.thumbnail?.alt?.ar : project.thumbnail?.alt?.en) || title;

  const category = project.category;
  const categoryLabel = (locale === 'ar' ? category?.name?.ar : category?.name?.en) || category?.name?.en || '';
  const categorySlug = category?.slug ? (locale === 'ar' ? category.slug.ar || category.slug.en : category.slug.en) : '';

  const hasThumbnail = !!project.thumbnail?.url &&
    !project.thumbnail.url.includes('/undefined');

  const bgClass     = cardBg === 'bg' ? 'bg-(--bg)' : 'bg-(--surface)';
  const thumbBgClass = cardBg === 'bg' ? 'bg-(--surface)' : 'bg-(--bg)';

  const baseClass = `
    group relative flex flex-col rounded-xl border border-(--border) ${bgClass} overflow-hidden
    hover:border-primary-500/30 hover:shadow-xl
    transition-[box-shadow,border-color] duration-300 will-change-transform
    ${className}
  `;

  const cardContent = (
    <>
      {/* Thumbnail */}
      <div className={`relative h-44 ${thumbBgClass} overflow-hidden`}>
        {hasThumbnail ? (
          <ImageWithFallback
            src={project.thumbnail!.url}
            fallbackSrc="/project-image.png"
            alt={altText}
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width:640px) 100vw,(max-width:1024px) 50vw,33vw"
            priority={priority}
            fixedWidth={fixedWidth}
          />
        ) : (
          <ImageWithFallback
            src="/project-image.png"
            fallbackSrc="/project-image.png"
            alt={title}
            className="object-cover"
            sizes="(max-width:640px) 100vw,(max-width:1024px) 50vw,33vw"
            priority={priority}
            fixedWidth={fixedWidth}
          />
        )}

        {/* Status badge — top start */}
        <div className="absolute top-3 inset-s-3">
          <Badge
            color={project.status === 'completed' ? 'success' : 'warning'}
            variant="solid" size="sm"
          >
            {project.status === 'completed' ? '✓' : '…'}
          </Badge>
        </div>

        {/* Featured badge — top end */}
        {showFeaturedBadge && project.featured && (
          <div className="absolute top-3 inset-e-3">
            <Badge color="primary" variant="solid" size="sm">★</Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-5 space-y-3">
        {showCategory && categoryLabel && (
          categorySlug ? (
            <Link
              href={`/${locale}/projects/category/${encodeURIComponent(categorySlug)}`}
              className="relative z-1 w-fit text-xs font-mono uppercase tracking-wider text-primary-500 hover:text-primary-400 transition-colors"
            >
              {categoryLabel}
            </Link>
          ) : (
            <span className="w-fit text-xs font-mono uppercase tracking-wider text-primary-500">{categoryLabel}</span>
          )
        )}
        <h3 className="font-semibold text-(--text) leading-snug group-hover:text-primary-500 transition-colors duration-180 line-clamp-2">
          {title}
        </h3>
        <p className="text-sm text-(--muted) leading-relaxed line-clamp-2">{desc}</p>

        {/* Tech stack */}
        <div className="flex flex-wrap gap-1.5">
          {project.techStack.slice(0, 4).map(tech => (
            <Badge key={tech} color="default" variant="soft" size="sm">{tech}</Badge>
          ))}
          {project.techStack.length > 4 && (
            <Badge color="default" variant="outline" size="sm">+{project.techStack.length - 4}</Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-1 inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors duration-180"
            >
              {labels.viewProject}
              <span className="sr-only"> {title}</span>
            </a>
          )}
          {project.repoUrl && (
            <a
              href={project.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-1 inline-flex items-center gap-1 text-xs font-medium text-(--muted) hover:text-(--text) transition-colors duration-180"
            >
              {labels.viewCode}
              <span className="sr-only"> {title}</span>
            </a>
          )}
          <Link
            href={`/${locale}/projects/${slug}`}
            className="ms-auto text-xs font-medium text-(--muted) hover:text-primary-500 transition-colors after:absolute after:inset-0"
          >
            {labels.details}
            {/* Descriptive text for SEO/SR — link-text audit uses text content. */}
            <span className="sr-only"> {title}</span>
          </Link>
        </div>
      </div>
    </>
  );

  if (animated) {
    return (
      <motion.article
        {...tilt}
        className={baseClass}
        style={{ transformStyle: 'preserve-3d', transition: 'transform 0.2s ease, box-shadow 0.3s ease, border-color 0.3s ease' }}
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, delay: animationDelay, ease: 'easeOut' }}
        viewport={{ once: true, amount: 0.15 }}
      >
        {cardContent}
      </motion.article>
    );
  }

  return (
    <article className={baseClass}>
      {cardContent}
    </article>
  );
}
